/**
 * FullSend Wishlist Module
 * Handles wishlist functionality for guests (localStorage) and logged-in users.
 * Follows v2 Design System patterns.
 *
 * Features:
 * - Add/remove products from wishlist
 * - localStorage persistence for guests
 * - Dynamic header icon count updates
 * - Full wishlist page functionality
 * - Add all to cart
 * - Clear all with confirmation
 * - Event dispatching for UI updates
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'fullsend_wishlist';
  const MAX_ITEMS = 100; // Maximum wishlist items

  const Wishlist = {
    items: [],
    initialized: false,

    /**
     * Initialize the wishlist module
     */
    init: function() {
      if (this.initialized) return;

      this.loadFromStorage();
      this.bindEvents();
      this.updateAllUI();
      this.initialized = true;

      console.log('[Wishlist] Initialized with', this.items.length, 'items');
    },

    /**
     * Load wishlist items from localStorage
     */
    loadFromStorage: function() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            this.items = parsed;
          }
        }
      } catch (error) {
        console.error('[Wishlist] Failed to load from storage:', error);
        this.items = [];
      }
    },

    /**
     * Save wishlist items to localStorage
     */
    saveToStorage: function() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (error) {
        console.error('[Wishlist] Failed to save to storage:', error);
      }
    },

    /**
     * Bind event listeners
     */
    bindEvents: function() {
      // Use event delegation for wishlist buttons
      document.addEventListener('click', this.handleClick.bind(this));

      // Listen for custom events
      document.addEventListener('wishlist:add', this.handleAddEvent.bind(this));
      document.addEventListener('wishlist:remove', this.handleRemoveEvent.bind(this));
    },

    /**
     * Handle click events via delegation
     */
    handleClick: function(event) {
      // Wishlist button toggle
      const wishlistButton = event.target.closest('[data-wishlist-button]');
      if (wishlistButton) {
        event.preventDefault();
        event.stopPropagation();
        this.toggleItem(wishlistButton);
        return;
      }

      // Remove from wishlist page
      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        const item = removeButton.closest('[data-wishlist-item]');
        if (item) {
          const productId = item.dataset.productId;
          this.removeItem(productId);
          this.animateRemoval(item);
        }
        return;
      }

      // Add to cart from wishlist
      const addToCartButton = event.target.closest('[data-wishlist-add-to-cart]');
      if (addToCartButton) {
        event.preventDefault();
        const item = addToCartButton.closest('[data-wishlist-item]');
        if (item) {
          const productId = item.dataset.productId;
          this.addToCart(productId, addToCartButton);
        }
        return;
      }

      // Add all to cart
      const addAllButton = event.target.closest('[data-wishlist-add-all-to-cart]');
      if (addAllButton) {
        event.preventDefault();
        this.addAllToCart(addAllButton);
        return;
      }

      // Clear all - show modal
      const clearAllButton = event.target.closest('[data-wishlist-clear-all]');
      if (clearAllButton) {
        event.preventDefault();
        this.showClearModal();
        return;
      }

      // Confirm clear
      const confirmClearButton = event.target.closest('[data-wishlist-confirm-clear]');
      if (confirmClearButton) {
        event.preventDefault();
        this.clearAll();
        this.hideClearModal();
        return;
      }

      // Close modal
      const closeModalButton = event.target.closest('[data-wishlist-modal-close]');
      if (closeModalButton) {
        event.preventDefault();
        this.hideClearModal();
        return;
      }
    },

    /**
     * Toggle item in wishlist
     */
    toggleItem: function(button) {
      const productId = button.dataset.productId;

      if (!productId) {
        console.error('[Wishlist] No product ID found on button');
        return;
      }

      if (this.hasItem(productId)) {
        this.removeItem(productId);
        this.updateButtonState(button, false);
      } else {
        const productData = this.getProductDataFromButton(button);
        this.addItem(productData);
        this.updateButtonState(button, true);
        this.animateButton(button);
      }
    },

    /**
     * Get product data from button attributes
     */
    getProductDataFromButton: function(button) {
      return {
        id: button.dataset.productId,
        handle: button.dataset.productHandle || '',
        title: button.dataset.productTitle || '',
        image: button.dataset.productImage || '',
        price: parseInt(button.dataset.productPrice, 10) || 0,
        comparePrice: parseInt(button.dataset.productComparePrice, 10) || 0,
        url: button.dataset.productUrl || '',
        available: button.dataset.productAvailable !== 'false',
        addedAt: Date.now()
      };
    },

    /**
     * Check if product is in wishlist
     */
    hasItem: function(productId) {
      return this.items.some(item => String(item.id) === String(productId));
    },

    /**
     * Add item to wishlist
     */
    addItem: function(productData) {
      if (this.hasItem(productData.id)) {
        return false;
      }

      if (this.items.length >= MAX_ITEMS) {
        console.warn('[Wishlist] Maximum items reached');
        return false;
      }

      this.items.push(productData);
      this.saveToStorage();
      this.updateHeaderCount();
      this.dispatchEvent('wishlist:updated', { action: 'add', item: productData });

      console.log('[Wishlist] Added item:', productData.title);
      return true;
    },

    /**
     * Remove item from wishlist
     */
    removeItem: function(productId) {
      const index = this.items.findIndex(item => String(item.id) === String(productId));

      if (index === -1) {
        return false;
      }

      const removed = this.items.splice(index, 1)[0];
      this.saveToStorage();
      this.updateHeaderCount();
      this.updateAllButtons();
      this.dispatchEvent('wishlist:updated', { action: 'remove', item: removed });

      console.log('[Wishlist] Removed item:', removed.title);
      return true;
    },

    /**
     * Clear all items from wishlist
     */
    clearAll: function() {
      this.items = [];
      this.saveToStorage();
      this.updateAllUI();
      this.dispatchEvent('wishlist:updated', { action: 'clear' });

      console.log('[Wishlist] Cleared all items');
    },

    /**
     * Update button visual state
     */
    updateButtonState: function(button, isActive) {
      button.setAttribute('aria-pressed', String(isActive));

      const text = button.querySelector('[data-wishlist-text]');
      if (text) {
        text.textContent = isActive ? 'Remove from Wishlist' : 'Add to Wishlist';
      }
    },

    /**
     * Animate button when added
     */
    animateButton: function(button) {
      button.classList.add('fs2-wishlist-btn--animating');
      setTimeout(() => {
        button.classList.remove('fs2-wishlist-btn--animating');
      }, 300);
    },

    /**
     * Update all wishlist buttons on page
     */
    updateAllButtons: function() {
      const buttons = document.querySelectorAll('[data-wishlist-button]');
      buttons.forEach(button => {
        const productId = button.dataset.productId;
        const isActive = this.hasItem(productId);
        this.updateButtonState(button, isActive);
      });
    },

    /**
     * Update header icon count
     */
    updateHeaderCount: function() {
      const count = this.items.length;
      const countElements = document.querySelectorAll('[data-wishlist-count]');
      const headerIcon = document.querySelector('[data-wishlist-header-icon]');

      countElements.forEach(el => {
        el.textContent = count;
        el.setAttribute('data-count', count);

        // Animate count change
        el.classList.add('fs2-wishlist-count--updated');
        setTimeout(() => {
          el.classList.remove('fs2-wishlist-count--updated');
        }, 300);
      });

      // Update screen reader text
      const countTextElements = document.querySelectorAll('[data-wishlist-count-text]');
      countTextElements.forEach(el => {
        el.textContent = count + ' items in wishlist';
      });

      // Update header icon state
      if (headerIcon) {
        headerIcon.setAttribute('data-has-items', count > 0 ? 'true' : 'false');
      }
    },

    /**
     * Update all UI elements
     */
    updateAllUI: function() {
      this.updateAllButtons();
      this.updateHeaderCount();
    },

    /**
     * Dispatch custom event
     */
    dispatchEvent: function(eventName, detail) {
      document.dispatchEvent(new CustomEvent(eventName, { detail: detail }));
    },

    /**
     * Handle custom add event
     */
    handleAddEvent: function(event) {
      if (event.detail && event.detail.productData) {
        this.addItem(event.detail.productData);
      }
    },

    /**
     * Handle custom remove event
     */
    handleRemoveEvent: function(event) {
      if (event.detail && event.detail.productId) {
        this.removeItem(event.detail.productId);
      }
    },

    // =========================================
    // Wishlist Page Functions
    // =========================================

    /**
     * Initialize wishlist page
     */
    initPage: function() {
      const pageElement = document.querySelector('[data-wishlist-page]');
      if (!pageElement) return;

      this.renderPage();
    },

    /**
     * Render wishlist page content
     */
    renderPage: function() {
      const loadingElement = document.querySelector('[data-wishlist-loading]');
      const emptyElement = document.querySelector('[data-wishlist-empty]');
      const contentElement = document.querySelector('[data-wishlist-content]');
      const gridElement = document.querySelector('[data-wishlist-grid]');
      const itemCountElement = document.querySelector('[data-wishlist-item-count]');

      // Hide loading
      if (loadingElement) {
        loadingElement.hidden = true;
      }

      // Update item count
      if (itemCountElement) {
        itemCountElement.textContent = this.items.length;
      }

      if (this.items.length === 0) {
        // Show empty state
        if (emptyElement) emptyElement.hidden = false;
        if (contentElement) contentElement.hidden = true;
      } else {
        // Show content
        if (emptyElement) emptyElement.hidden = true;
        if (contentElement) contentElement.hidden = false;

        // Render products
        if (gridElement) {
          this.renderProducts(gridElement);
        }
      }
    },

    /**
     * Render products in grid
     */
    renderProducts: function(gridElement) {
      const template = document.getElementById('wishlist-product-template');
      if (!template) {
        console.error('[Wishlist] Product template not found');
        return;
      }

      gridElement.innerHTML = '';

      this.items.forEach(item => {
        const clone = template.content.cloneNode(true);
        const article = clone.querySelector('[data-wishlist-item]');

        if (article) {
          article.dataset.productId = item.id;

          // Image
          const image = clone.querySelector('[data-wishlist-item-image]');
          if (image) {
            image.src = item.image;
            image.alt = item.title;
          }

          // Links
          const links = clone.querySelectorAll('[data-wishlist-item-link], [data-wishlist-item-title-link]');
          links.forEach(link => {
            link.href = item.url;
          });

          // Title
          const titleLink = clone.querySelector('[data-wishlist-item-title-link]');
          if (titleLink) {
            titleLink.textContent = item.title;
          }

          // Price
          const priceElement = clone.querySelector('[data-wishlist-item-price]');
          if (priceElement) {
            const hasComparePrice = item.comparePrice && item.comparePrice > item.price;
            if (hasComparePrice) {
              priceElement.classList.add('fs2-wishlist-item__price--sale');
              priceElement.innerHTML = `
                <span class="fs2-wishlist-item__price-current">${this.formatMoney(item.price)}</span>
                <span class="fs2-wishlist-item__price-compare">${this.formatMoney(item.comparePrice)}</span>
              `;
            } else {
              priceElement.innerHTML = `
                <span class="fs2-wishlist-item__price-current">${this.formatMoney(item.price)}</span>
              `;
            }
          }

          // Availability
          const availabilityElement = clone.querySelector('[data-wishlist-item-availability]');
          if (availabilityElement) {
            if (item.available) {
              availabilityElement.textContent = 'In Stock';
              availabilityElement.classList.add('fs2-wishlist-item__availability--in-stock');
            } else {
              availabilityElement.textContent = 'Out of Stock';
              availabilityElement.classList.add('fs2-wishlist-item__availability--out-of-stock');
            }
          }

          // Add to cart button
          const addButton = clone.querySelector('[data-wishlist-add-to-cart]');
          if (addButton && !item.available) {
            addButton.disabled = true;
            addButton.textContent = 'Out of Stock';
          }
        }

        gridElement.appendChild(clone);
      });
    },

    /**
     * Animate item removal
     */
    animateRemoval: function(element) {
      element.classList.add('fs2-wishlist-item--removing');
      setTimeout(() => {
        element.remove();
        this.updatePageAfterRemoval();
      }, 300);
    },

    /**
     * Update page after item removal
     */
    updatePageAfterRemoval: function() {
      const itemCountElement = document.querySelector('[data-wishlist-item-count]');
      if (itemCountElement) {
        itemCountElement.textContent = this.items.length;
      }

      // Show empty state if no items
      if (this.items.length === 0) {
        const emptyElement = document.querySelector('[data-wishlist-empty]');
        const contentElement = document.querySelector('[data-wishlist-content]');
        if (emptyElement) emptyElement.hidden = false;
        if (contentElement) contentElement.hidden = true;
      }
    },

    /**
     * Add single item to cart
     */
    addToCart: async function(productId, button) {
      const item = this.items.find(i => String(i.id) === String(productId));
      if (!item) return;

      // Get the first available variant
      // For simplicity, we'll fetch the product data
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      try {
        // Fetch product to get variant ID
        const response = await fetch(`/products/${item.handle}.js`);
        if (!response.ok) throw new Error('Failed to fetch product');

        const product = await response.json();
        const variant = product.variants.find(v => v.available) || product.variants[0];

        if (!variant) {
          throw new Error('No variant available');
        }

        // Add to cart
        const cartResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variant.id,
            quantity: 1
          })
        });

        if (!cartResponse.ok) throw new Error('Failed to add to cart');

        button.textContent = 'Added!';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);

        // Trigger cart update
        document.dispatchEvent(new CustomEvent('cart:add'));

        // Open cart drawer if available
        if (window.FullSend && window.FullSend.CartDrawer) {
          window.FullSend.CartDrawer.refresh();
          window.FullSend.CartDrawer.open();
        }

      } catch (error) {
        console.error('[Wishlist] Failed to add to cart:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    },

    /**
     * Add all items to cart
     */
    addAllToCart: async function(button) {
      const availableItems = this.items.filter(item => item.available);

      if (availableItems.length === 0) {
        alert('No items available to add to cart.');
        return;
      }

      button.disabled = true;
      const originalText = button.innerHTML;
      button.innerHTML = '<span>Adding...</span>';

      let successCount = 0;
      let errorCount = 0;

      for (const item of availableItems) {
        try {
          // Fetch product to get variant ID
          const response = await fetch(`/products/${item.handle}.js`);
          if (!response.ok) throw new Error('Failed to fetch product');

          const product = await response.json();
          const variant = product.variants.find(v => v.available) || product.variants[0];

          if (!variant) {
            errorCount++;
            continue;
          }

          // Add to cart
          const cartResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variant.id,
              quantity: 1
            })
          });

          if (cartResponse.ok) {
            successCount++;
          } else {
            errorCount++;
          }

        } catch (error) {
          console.error('[Wishlist] Failed to add item to cart:', error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        button.innerHTML = `<span>${successCount} Added!</span>`;

        // Trigger cart update
        document.dispatchEvent(new CustomEvent('cart:add'));

        // Open cart drawer if available
        if (window.FullSend && window.FullSend.CartDrawer) {
          window.FullSend.CartDrawer.refresh();
          window.FullSend.CartDrawer.open();
        }
      }

      if (errorCount > 0) {
        console.warn(`[Wishlist] Failed to add ${errorCount} items`);
      }

      setTimeout(() => {
        button.innerHTML = originalText;
        button.disabled = false;
      }, 3000);
    },

    /**
     * Show clear all confirmation modal
     */
    showClearModal: function() {
      const modal = document.querySelector('[data-wishlist-clear-modal]');
      if (modal) {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';

        // Focus first button
        const firstButton = modal.querySelector('button');
        if (firstButton) firstButton.focus();
      }
    },

    /**
     * Hide clear all confirmation modal
     */
    hideClearModal: function() {
      const modal = document.querySelector('[data-wishlist-clear-modal]');
      if (modal) {
        modal.hidden = true;
        document.body.style.overflow = '';
      }
    },

    /**
     * Format money value
     */
    formatMoney: function(cents) {
      if (typeof cents === 'string') {
        cents = parseInt(cents, 10);
      }

      const dollars = (cents / 100).toFixed(2);
      return '$' + dollars;
    },

    // =========================================
    // Public API
    // =========================================

    /**
     * Get all wishlist items
     */
    getItems: function() {
      return [...this.items];
    },

    /**
     * Get wishlist count
     */
    getCount: function() {
      return this.items.length;
    },

    /**
     * Check if product is in wishlist (public method)
     */
    isInWishlist: function(productId) {
      return this.hasItem(productId);
    }
  };

  // Initialize when DOM is ready
  function init() {
    Wishlist.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose to global namespace
  window.FullSend = window.FullSend || {};
  window.FullSend.Wishlist = Wishlist;

})();
